import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faClock, faEarListen, faHeadphones, faSliders } from '@fortawesome/free-solid-svg-icons';
import type { Heading } from '../types/global';
import type { HowWeWorkItem, ServiceItem, processItem } from '../types/services';

interface ServiceAside {
    heading: string
    items: {
        icon: IconProp
        description: string
    }[]
}

export const servicesHeading: Heading = {
    heading: 'Services',
    paragraph: 'From first demos to full-length records, Strangeways Studios offers recording, production, rehearsal, and remote sessions from our 1,500 sq ft studio on Vancouver Island.',
}

export const aside: ServiceAside = {
    heading: "Why Strangeways",
    items: [
    {
    icon: faClock,
    description: '<b>10+ years in business</b> serving independent artists through writing, production, mixing, and mastering.',
    },
    {
    icon: faHeadphones,
    description: '<b>Performance-first workflow</b> with four recallable stereo monitor mixes so you\'re ready to record within minutes of walking through the door.',
    },
    {
    icon: faSliders,
    description: '<b>Remote collaboration</b> with Sessionwire so producers, bandmates, or clients can join sessions from anywhere in the world.',
    },
    {
    icon: faEarListen,
    description: '<b>Flexible sound stage</b> that works equally well for rock bands, choirs, jazz ensembles, and chamber groups.',
    }
    ]
}


export const services: ServiceItem[] = [
    {
        id: 'recording-production',
        icon: 'faMicrophoneLines',
        title: 'Recording & production',
        description:
            'Songwriting, overdubs, and full-album production in a treated studio built for live bands and detailed edits.',
        details: [
            '1,500 sq ft facility with a 750 sq ft sound stage for live-off-the-floor tracking.',
            '<b>Antelope Orion Studio Synergy Core</b> conversion with Trident-powered Audient preamps.',
            'Four individual stereo headphone mixes with zero-latency FPGA effects and amp modelling.',
            'Remote collaborators can join via Sessionwire with bi-directional, high-quality 2-channel audio pass-through between studios.',
        ],
    },
    {
        id: 'mixing-mastering',
        icon: 'faRecordVinyl',
        title: 'Mixing & mastering',
        description:
            'Hybrid in-the-box and outboard mixing with mastering-grade conversion and an accurate listening environment.',
        details: [
            '<b>Mastering-grade converters</b> with a noise floor down to -130 dB for detailed, transparent monitoring.',
            'Balanced, acoustically treated control room for critical mix decisions that translate.',
            'Flexible delivery: singles, EPs, full albums, stems, and instrumental/TV mixes.',
            'Clients can sit in on mixing sessions remotely via Sessionwire with 400 Kbps high-quality audio pass-through.',
        ],
    },
    {
        id: 'rehearsal-preproduction',
        icon: 'faDrum',
        title: 'Rehearsal & pre-production',
        description:
            'Use the 750 sq ft sound stage as your rehearsal room with studio-grade PA, backline, and recallable monitor mixes.',
        details: [
            'Three floor wedges with 15-inch speakers plus a subwoofer for clear stage-style monitoring.',
            'Three vocal mics, a selection of guitar and bass amps, and a Taye drum kit included with the room.',
            'In-ear monitor setups welcome—bring your own IEMs and we’ll wire you in.',
            'Monitor mix configurations can be saved and recalled across successive sessions for a consistent sound.',
        ],
    },
    {
        id: 'mobile-recording',
        icon: 'faTowerBroadcast',
        title: 'Mobile recording & broadcast',
        description:
            'Bring the Strangeways rig to concert halls, churches, theatres, or other favourite rooms for on-location recording and broadcast.',
        details: [
            'Portable road-case system with Mac Pro, Antelope conversion, and Audient Trident preamps.',
            'Multi-mic capture for ensembles, bands, choirs, and live events in acoustically interesting spaces.',
            'Optional multi-camera HD capture and live switching for performances, sessions, and livestreams.',
            'Custom quotes for location, scale, and technical requirements—tell us about your venue and goals.',
        ],
    },
]

export const howWeWorkHeading: Heading = {
    heading: 'How we work',
    paragraph: 'Start with a free 30-minute consult, then book your session and 50% deposit online. After recording, everything is delivered through your own Nextcloud account for easy collaboration.',
}

export const howWeWorkItems: HowWeWorkItem[] = [
    {
        id: 'consult',
        title: 'Free 30-minute consult',
        summary: 'Start with a no-pressure call to shape your session.',
        details: [
            'Book a free 30-minute consultation to ask questions and talk through your goals.',
            'Discuss songs, arrangements, references, and what you want to leave with.',
            'We’ll recommend session length, setup, and any pre-production to get the most from your time.',
            'Perfect for first-time studio visits or new projects.',
        ],
    },
    {
        id: 'book-session',
        title: 'Book your session',
        summary: 'Choose a date and lock it in with a 50% deposit.',
        details: [
            'Use the booking calendar on this page to pick a day and time that works for your band.',
            'Secure the session with a non-refundable 50% deposit; the remaining 50% is due at the end of the day.',
            'We’ll confirm your booking, send a brief checklist, and answer any last-minute questions.',
            'Need to reschedule? We’ll move you to a comparable time slot at no extra cost.',
        ],
    },
    {
        id: 'session-delivery',
        title: 'Session & delivery',
        summary: 'Record, then access everything via your own account.',
        details: [
            'Record in <b>Logic Pro X</b>, <b>Ableton Live 11</b>, or <b>Reaper</b>; Pro Tools can be arranged on request for a small additional fee.',
            '<b>Instant backup</b>: project and audio files are uploaded to our secure Nextcloud server straight after the session.',
            'You’ll receive your own Nextcloud account for ongoing collaboration during production and mixing.',
            'Download via share links or sync everything to your computer with the Nextcloud app.',
        ],
    },
]

export const processHeading = "Process"

export const processes: processItem[] = [
    {
        id: "consult",
        heading: 'Consult',
        paragraph: 'Book a free 30-minute call to talk through your songs, goals, and budget, and decide what kind of session you need.',
    },
    {
        id: "book",
        heading: 'Book & deposit',
        paragraph: 'Choose a date and time with the online calendar and secure it with a 50% non-refundable deposit; the balance is due at the end of the session day.',
    },
    {
        id: "record",
        heading: "Record",
        paragraph: "Track in <b>Logic Pro X</b>, <b>Ableton Live 11</b>, or <b>Reaper</b> (Pro Tools available on request), with live-off-the-floor options and flexible overdubs."
    },
    {
        id: "deliver",
        heading: "Deliver & collaborate",
        paragraph: "We upload project and audio files to your Nextcloud account so you can download or sync them and keep collaborating through mix and master."
    }
]